import React, { useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  children: React.ReactNode;
  title: string;
  placement?: "top" | "bottom" | "left" | "right";
  className?: string;
}

function RenderTooltip({
  children,
  title,
  placement = "top",
  className = "",
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipOffset = 8;

      let top = 0;
      let left = 0;

      switch (placement) {
        case "bottom":
          top = rect.bottom + tooltipOffset;
          left = rect.left + rect.width / 2;
          break;
        case "left":
          top = rect.top + rect.height / 2;
          left = rect.left - tooltipOffset;
          break;
        case "right":
          top = rect.top + rect.height / 2;
          left = rect.right + tooltipOffset;
          break;
        default: // top
          top = rect.top - tooltipOffset;
          left = rect.left + rect.width / 2;
          break;
      }

      setPosition({ top, left });
    }
  }, [isVisible, placement]);

  const getTooltipStyles = () => {
    const baseStyles: React.CSSProperties = {
      position: "fixed",
      zIndex: 9999,
      backgroundColor: "#343a40",
      color: "white",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      whiteSpace: "nowrap",
      pointerEvents: "none",
      top: position.top,
      left: position.left,
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    };

    switch (placement) {
      case "bottom":
        return {
          ...baseStyles,
          transform: "translateX(-50%)",
        };
      case "left":
        return {
          ...baseStyles,
          transform: "translate(-100%, -50%)",
        };
      case "right":
        return {
          ...baseStyles,
          transform: "translateY(-50%)",
        };
      default: // top
        return {
          ...baseStyles,
          transform: "translate(-50%, -100%)",
        };
    }
  };

  const getArrowStyles = () => {
    const baseArrowStyles: React.CSSProperties = {
      position: "absolute",
      width: 0,
      height: 0,
    };

    switch (placement) {
      case "bottom":
        return {
          ...baseArrowStyles,
          bottom: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          borderLeft: "4px solid transparent",
          borderRight: "4px solid transparent",
          borderBottom: "4px solid #343a40",
        };
      case "left":
        return {
          ...baseArrowStyles,
          left: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          borderTop: "4px solid transparent",
          borderBottom: "4px solid transparent",
          borderLeft: "4px solid #343a40",
        };
      case "right":
        return {
          ...baseArrowStyles,
          right: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          borderTop: "4px solid transparent",
          borderBottom: "4px solid transparent",
          borderRight: "4px solid #343a40",
        };
      default: // top
        return {
          ...baseArrowStyles,
          top: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          borderLeft: "4px solid transparent",
          borderRight: "4px solid transparent",
          borderTop: "4px solid #343a40",
        };
    }
  };

  const renderTooltip = () => {
    if (!isVisible) return null;

    return (
      <div style={getTooltipStyles()} className={className}>
        {title}
        <div style={getArrowStyles()} />
      </div>
    );
  };

  return (
    <div
      ref={triggerRef}
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {typeof document !== "undefined" &&
        createPortal(renderTooltip(), document.body)}
    </div>
  );
}

export default RenderTooltip;
